/*
 * Product media gallery.
 *
 * The track does the actual work: it is a scroll-snap container, so swipe,
 * trackpad and arrow keys all work with this file absent. Everything here is
 * additive, which is why the buttons ship aria-hidden with tabindex -1 and only
 * become real controls once this runs. A page with no JS gets a swipeable
 * gallery rather than a broken one.
 *
 * Position is read back off scrollLeft rather than tracked in a variable. The
 * user can scroll the track directly, so any counter we kept here would drift
 * out of step with what is actually on screen the first time they swiped.
 */
(function () {
  'use strict';

  var REDUCED = '(prefers-reduced-motion: reduce)';

  document.querySelectorAll('[data-peanut-gallery]').forEach(function (root) {
    var track = root.querySelector('[data-peanut-gallery-track]');
    var slides = Array.prototype.slice.call(
      root.querySelectorAll('[data-peanut-gallery-slide]')
    );
    var prev = root.querySelector('[data-peanut-gallery-prev]');
    var next = root.querySelector('[data-peanut-gallery-next]');
    var thumbs = Array.prototype.slice.call(
      root.querySelectorAll('[data-peanut-gallery-thumb]')
    );

    if (!track || slides.length < 2) return;

    root.classList.add('peanut-gallery--enabled');

    // Hand the controls back to the keyboard and the accessibility tree now
    // that they do something.
    [prev, next].concat(thumbs).forEach(function (el) {
      if (!el) return;
      el.removeAttribute('aria-hidden');
      el.removeAttribute('tabindex');
    });

    function behavior() {
      return window.matchMedia(REDUCED).matches ? 'auto' : 'smooth';
    }

    // Slide width including the gap, measured rather than assumed, so the CSS
    // can change the gap without this file needing to know.
    function step() {
      if (slides.length < 2) return track.clientWidth;
      var delta = slides[1].offsetLeft - slides[0].offsetLeft;
      return delta > 0 ? delta : track.clientWidth;
    }

    function current() {
      return Math.round(track.scrollLeft / step());
    }

    // The arrows glide one slide. A thumbnail jumps: it can be six slides away,
    // and a smooth scroll across all of them took about a second, measured at
    // 957ms, with every image in between dragged past on the way.
    function goTo(index, instant) {
      var clamped = Math.max(0, Math.min(index, slides.length - 1));
      track.scrollTo({ left: clamped * step(), behavior: instant ? 'instant' : behavior() });
    }

    var lastIndex = -1;

    function sync() {
      var index = current();

      // scrollLeft lands a pixel or two short of the end on fractional widths,
      // so the last slide is detected by remaining distance rather than by
      // index. Without this the next arrow never disables.
      var atStart = track.scrollLeft <= 1;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

      if (prev) prev.disabled = atStart;
      if (next) next.disabled = atEnd;

      thumbs.forEach(function (thumb, i) {
        var on = i === index;
        thumb.classList.toggle('peanut-gallery__thumb--on', on);
        // removed rather than set false, so a screen reader hears one current
        // thumbnail rather than seven with six of them denied
        if (on) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });

      // Keep the current thumbnail in its strip. On a phone five of seven fit,
      // so swiping to the sixth image left its thumbnail off the edge. Moved by
      // scrollLeft on the strip itself, never scrollIntoView, which would also
      // scroll the page. Only when the slide actually changed, and not on the
      // first pass.
      if (index !== lastIndex) {
        var strip = thumbs[index] && thumbs[index].parentNode && thumbs[index].parentNode.parentNode;
        var item = thumbs[index];
        if (strip && item && lastIndex !== -1) {
          // Measured from rects: offsetLeft is relative to whichever ancestor
          // happens to be positioned, which is not the strip.
          //
          // Both axes since 2026-10-11. The strip runs sideways under the
          // photograph below 1024px and down its right side from there, and
          // this only ever moved it sideways, so in the rail a thumbnail past
          // the bottom edge stayed there. A strip scrolls on one axis only, so
          // the other target clamps to nothing.
          var itemRect = item.getBoundingClientRect();
          var stripRect = strip.getBoundingClientRect();
          var left = itemRect.left - stripRect.left + strip.scrollLeft;
          var top = itemRect.top - stripRect.top + strip.scrollTop;
          strip.scrollTo({
            left: Math.max(0, left - (strip.clientWidth - item.offsetWidth) / 2),
            top: Math.max(0, top - (strip.clientHeight - item.offsetHeight) / 2),
            behavior: behavior()
          });
        }
        lastIndex = index;
      }
    }

    if (prev) {
      prev.addEventListener('click', function () {
        goTo(current() - 1);
      });
    }

    if (next) {
      next.addEventListener('click', function () {
        goTo(current() + 1);
      });
    }

    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(parseInt(thumb.getAttribute('data-peanut-gallery-thumb'), 10) || 0, true);
      });
    });

    // Scroll fires far faster than paint. Coalescing to one rAF keeps this off
    // the critical path during a flick on a phone.
    var queued = false;
    track.addEventListener(
      'scroll',
      function () {
        if (queued) return;
        queued = true;
        window.requestAnimationFrame(function () {
          queued = false;
          sync();
        });
      },
      { passive: true }
    );

    window.addEventListener('resize', sync);

    sync();
  });
})();
